import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { interviewActions } from 'src/redux/slices/Interview/interviewSlice';
import { useIPE } from 'src/redux/slices/Interview/hooks';
import { makeStepKey } from 'src/redux/slices/Interview/stepKey';
import axiosInstance from 'src/utils/axios';
import type {
  InterviewState,
  UserProfileField,
  HistoryStep,
} from 'src/redux/slices/Interview/types';
import SectionLabel from '../components/SectionLabel';
import QuestionText from '../components/QuestionText';
import HelpText from '../components/HelpText';
import NameGroupInput from '../components/inputs/NameGroupInput';
import ChoicesInput from '../components/inputs/ChoicesInput';
import MultiselectInput from '../components/inputs/MultiselectInput';
import DateInput from '../components/inputs/DateInput';
import SSNInput from '../components/inputs/SSNInput';
import InterviewFooter from '../components/InterviewFooter';
import ContentCard from '../components/ContentCard';
import { deepEqual } from './reviewNav';

type RootState = { interview: InterviewState };

export default function UserProfileFieldScreen(): React.ReactElement {
  const dispatch = useDispatch();
  const {
    userProfileFields,
    profileFieldsByName,
    deferred,
    surveyId,
    onBehalfOf,
    history,
    cursor,
    answersByKey,
    frontierResyncNeeded,
    loading,
  } = useSelector((state: RootState) => state.interview);
  const { callIPE } = useIPE();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [localValue, setLocalValue] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  const isReviewing = history.length > 0 && cursor < history.length;

  // Refs so the document-level Enter listener always invokes the latest
  // handlers without re-registering on every render.
  const saveRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const reviewingNextRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const isReviewingRef = useRef(false);
  isReviewingRef.current = isReviewing;
  // Synchronous gate so rapid Enter/clicks can't fire duplicate saves before
  // the `saving` state re-renders (mirrors QuestionScreen's gateInFlightRef).
  const gateInFlightRef = useRef(false);

  // Match QuestionScreen: Enter on the document triggers Save & Next (frontier)
  // or Next (reviewing). Ignored while loading, in textareas, or contentEditable.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key !== 'Enter') {
        return;
      }
      if (loading) {
        return;
      }
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      if (tag === 'textarea') {
        return;
      }
      if ((e.target as HTMLElement)?.isContentEditable) {
        return;
      }
      e.preventDefault();
      if (isReviewingRef.current) {
        reviewingNextRef.current?.();
      } else {
        saveRef.current?.();
      }
    },
    [loading]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // ── frontier resolver ─────
  const currentField: UserProfileField | undefined = userProfileFields.find(
    (f: UserProfileField) => !deferred.profileFieldNames.includes(f.field_name)
  );

  // ── reviewing resolver (computed unconditionally) ─────
  const reviewedStep: HistoryStep | null =
    isReviewing && history[cursor].kind === 'user_profile_field'
      ? history[cursor]
      : null;

  const reviewedStepKey = reviewedStep ? makeStepKey(reviewedStep) : null;
  const reviewedStoredValue =
    reviewedStepKey && reviewedStepKey in answersByKey
      ? answersByKey[reviewedStepKey]
      : null;

  const reviewedField: UserProfileField | undefined = reviewedStep
    ? profileFieldsByName[reviewedStep.fieldName!] ??
      userProfileFields.find(
        (f: UserProfileField) => f.field_name === reviewedStep.fieldName
      )
    : undefined;

  // Reset localValue when the active field changes (frontier or reviewing)
  const activeFieldName = isReviewing
    ? reviewedStep?.fieldName ?? null
    : currentField?.field_name ?? null;
  useEffect(() => {
    setLocalValue(null);
  }, [activeFieldName]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const reviewingValue: any = reviewedField
    ? localValue ?? reviewedStoredValue ?? reviewedField.existing_value
    : null;

  const handleReviewingProfileNext = useCallback(async () => {
    if (gateInFlightRef.current) {
      return;
    }
    if (!reviewedStep || !reviewedField || !surveyId) {
      return;
    }

    let current: unknown;
    if (reviewedField.question_type === 'name_group') {
      current = localValue ??
        reviewedStoredValue ?? {
          first_name: reviewedField.existing_value?.first_name ?? '',
          middle_name: reviewedField.existing_value?.middle_name ?? '',
          last_name: reviewedField.existing_value?.last_name ?? '',
        };
    } else {
      current =
        localValue ?? reviewedStoredValue ?? reviewedField.existing_value;
    }

    const stored = reviewedStoredValue ?? reviewedField.existing_value;

    if (deepEqual(current, stored)) {
      const nextIndex = cursor + 1;
      if (nextIndex >= history.length) {
        if (frontierResyncNeeded) {
          dispatch(interviewActions.setFrontierResyncNeeded(false));
          dispatch(interviewActions.setWorkItemType('loading'));
          dispatch(interviewActions.setLoading(true));
          callIPE();
        } else {
          dispatch(interviewActions.moveCursorForward());
        }
      } else {
        dispatch(interviewActions.moveCursorForward());
      }
      return;
    }

    gateInFlightRef.current = true;
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fields: Record<string, any> = {};
      if (reviewedField.question_type === 'name_group') {
        const nameGroup = current as {
          first_name?: string;
          middle_name?: string;
          last_name?: string;
        };
        fields.first_name = nameGroup?.first_name ?? '';
        fields.middle_name = nameGroup?.middle_name ?? '';
        fields.last_name = nameGroup?.last_name ?? '';
      } else {
        fields[reviewedStep.fieldName!] = current;
      }

      await axiosInstance.patch('/v2/interview/user-profile/', {
        survey_id: surveyId,
        on_behalf_of: onBehalfOf || undefined,
        fields,
      });

      const savedValue =
        reviewedField.question_type === 'name_group'
          ? {
              first_name: fields.first_name,
              middle_name: fields.middle_name,
              last_name: fields.last_name,
            }
          : current;

      dispatch(
        interviewActions.recordProfileStep({
          fieldName: reviewedStep.fieldName!,
          value: savedValue,
        })
      );
      dispatch(interviewActions.setFrontierResyncNeeded(true));

      const nextIndex = cursor + 1;
      if (nextIndex >= history.length) {
        dispatch(interviewActions.setFrontierResyncNeeded(false));
        dispatch(interviewActions.setWorkItemType('loading'));
        dispatch(interviewActions.setLoading(true));
        callIPE();
      } else {
        dispatch(interviewActions.moveCursorForward());
      }
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { detail?: string; fields?: unknown } };
      };
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.fields ||
        'Failed to save.';
      dispatch(
        interviewActions.setAnswerError(
          typeof detail === 'string' ? detail : JSON.stringify(detail)
        )
      );
    } finally {
      gateInFlightRef.current = false;
    }
  }, [
    reviewedStep,
    reviewedField,
    localValue,
    reviewedStoredValue,
    surveyId,
    onBehalfOf,
    cursor,
    history.length,
    frontierResyncNeeded,
    dispatch,
    callIPE,
  ]);
  reviewingNextRef.current = handleReviewingProfileNext;

  const handleBack = useCallback(() => {
    dispatch(interviewActions.moveCursorBack());
  }, [dispatch]);

  // ── reviewing mode ───────────────────
  if (isReviewing && reviewedStep && reviewedField) {
    const renderReviewInput = (): React.ReactElement => {
      const value = reviewingValue;
      switch (reviewedField.question_type) {
        case 'name_group':
          return (
            <NameGroupInput
              value={
                value || {
                  first_name: '',
                  middle_name: '',
                  last_name: '',
                }
              }
              onChange={setLocalValue}
            />
          );
        case 'choices':
          return (
            <ChoicesInput
              options={reviewedField.options || []}
              value={value}
              onChange={setLocalValue}
            />
          );
        case 'multiselect':
          return (
            <MultiselectInput
              options={reviewedField.options || []}
              value={value || []}
              onChange={setLocalValue}
            />
          );
        case 'date':
          return <DateInput value={value || ''} onChange={setLocalValue} />;
        case 'ssn':
          return <SSNInput value={value || ''} onChange={setLocalValue} />;
        default:
          return <Box>Unknown field type: {reviewedField.question_type}</Box>;
      }
    };

    return (
      <Box
        data-testid="user-profile-field-screen"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            minHeight: 0,
            display: 'flex',
            justifyContent: { md: 'center' },
            alignItems: { md: 'flex-start' },
            pt: { md: 4 },
          }}
        >
          <ContentCard>
            <SectionLabel label="Your Profile" />
            <QuestionText text={reviewedField.question_text} />
            <HelpText text={reviewedField.help_text} />
            {renderReviewInput()}
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                justifyContent: 'flex-end',
                gap: 2,
              }}
            >
              {cursor > 0 && (
                <Button
                  onClick={handleBack}
                  data-testid="profile-back-button-inline"
                  sx={{
                    height: 44,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    fontSize: 14,
                    fontWeight: 500,
                    px: 2.5,
                    textTransform: 'none',
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                onClick={handleReviewingProfileNext}
                data-testid="profile-next-button-inline"
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  fontSize: 16,
                  fontWeight: 600,
                  px: 3,
                  textTransform: 'none',
                }}
              >
                Next
              </Button>
            </Box>
          </ContentCard>
        </Box>
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {cursor > 0 ? (
            <InterviewFooter
              primaryLabel="Next"
              onPrimary={handleReviewingProfileNext}
              primaryTestId="profile-next-button"
              tertiaryLabel="Back"
              onTertiary={handleBack}
              tertiaryTestId="profile-back-button"
            />
          ) : (
            <InterviewFooter
              primaryLabel="Next"
              onPrimary={handleReviewingProfileNext}
              primaryTestId="profile-next-button"
            />
          )}
        </Box>
      </Box>
    );
  }

  if (isReviewing) {
    return <Box data-testid="user-profile-field-screen" />;
  }

  // ── frontier mode ───────────────────

  if (!currentField) {
    return <Box data-testid="user-profile-field-screen" />;
  }

  const handleSave = async (): Promise<void> => {
    if (gateInFlightRef.current) {
      return;
    }
    gateInFlightRef.current = true;
    setSaving(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fields: Record<string, any> = {};
      if (currentField.question_type === 'name_group') {
        fields.first_name = localValue?.first_name || '';
        fields.middle_name = localValue?.middle_name || '';
        fields.last_name = localValue?.last_name || '';
      } else {
        fields[currentField.field_name] = localValue;
      }

      await axiosInstance.patch('/v2/interview/user-profile/', {
        survey_id: surveyId,
        on_behalf_of: onBehalfOf || undefined,
        fields,
      });

      const savedValue =
        currentField.question_type === 'name_group'
          ? {
              first_name: fields.first_name,
              middle_name: fields.middle_name,
              last_name: fields.last_name,
            }
          : localValue ?? currentField.existing_value;

      dispatch(
        interviewActions.recordProfileStep({
          fieldName: currentField.field_name,
          value: savedValue,
        })
      );

      setLocalValue(null);
      dispatch(interviewActions.setLoading(true));
      callIPE();
    } catch (error: unknown) {
      const err = error as {
        response?: { data?: { detail?: string; fields?: unknown } };
      };
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.fields ||
        'Failed to save.';
      dispatch(
        interviewActions.setAnswerError(
          typeof detail === 'string' ? detail : JSON.stringify(detail)
        )
      );
    } finally {
      gateInFlightRef.current = false;
      setSaving(false);
    }
  };
  saveRef.current = handleSave;

  const handleSkip = (): void => {
    dispatch(
      interviewActions.skipProfileStep({
        fieldName: currentField.field_name,
      })
    );
    dispatch(interviewActions.setLoading(true));
    callIPE();
  };

  const renderInput = (): React.ReactElement => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const value: any = localValue ?? currentField.existing_value;
    switch (currentField.question_type) {
      case 'name_group':
        return (
          <NameGroupInput
            value={value || { first_name: '', middle_name: '', last_name: '' }}
            onChange={setLocalValue}
          />
        );
      case 'choices':
        return (
          <ChoicesInput
            options={currentField.options || []}
            value={value}
            onChange={setLocalValue}
          />
        );
      case 'multiselect':
        return (
          <MultiselectInput
            options={currentField.options || []}
            value={value || []}
            onChange={setLocalValue}
          />
        );
      case 'date':
        return <DateInput value={value || ''} onChange={setLocalValue} />;
      case 'ssn':
        return <SSNInput value={value || ''} onChange={setLocalValue} />;
      default:
        return <Box>Unknown field type: {currentField.question_type}</Box>;
    }
  };

  const showBack = cursor > 0;

  return (
    <Box
      data-testid="user-profile-field-screen"
      sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          minHeight: 0,
          display: 'flex',
          justifyContent: { md: 'center' },
          alignItems: { md: 'flex-start' },
          pt: { md: 4 },
        }}
      >
        <ContentCard>
          <SectionLabel label="Your Profile" />
          <QuestionText text={currentField.question_text} />
          <HelpText text={currentField.help_text} />
          {renderInput()}
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              justifyContent: 'flex-end',
              gap: 2,
            }}
          >
            {showBack && (
              <Button
                onClick={handleBack}
                data-testid="profile-back-button-inline"
                sx={{
                  height: 44,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: 'grey.300',
                  color: 'text.secondary',
                  fontSize: 14,
                  fontWeight: 500,
                  px: 2.5,
                  textTransform: 'none',
                }}
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleSkip}
              data-testid="profile-skip-button-inline"
              sx={{
                height: 44,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: 'grey.300',
                color: 'text.secondary',
                fontSize: 14,
                fontWeight: 500,
                px: 2.5,
                textTransform: 'none',
              }}
            >
              Skip for now
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving}
              data-testid="profile-save-button-inline"
              startIcon={
                saving ? (
                  <CircularProgress size={16} color="inherit" />
                ) : undefined
              }
              sx={{
                height: 44,
                borderRadius: '12px',
                fontSize: 16,
                fontWeight: 600,
                px: 3,
                textTransform: 'none',
              }}
            >
              <span>Save &amp; Next Question</span>
            </Button>
          </Box>
        </ContentCard>
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {showBack ? (
          <InterviewFooter
            primaryLabel="Save & Next Question"
            secondaryLabel="Skip for now"
            onPrimary={handleSave}
            onSecondary={handleSkip}
            primaryDisabled={saving}
            primaryStartIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            primaryTestId="profile-save-button"
            secondaryTestId="profile-skip-button"
            tertiaryLabel="Back"
            onTertiary={handleBack}
            tertiaryTestId="profile-back-button"
          />
        ) : (
          <InterviewFooter
            primaryLabel="Save & Next Question"
            secondaryLabel="Skip for now"
            onPrimary={handleSave}
            onSecondary={handleSkip}
            primaryDisabled={saving}
            primaryStartIcon={
              saving ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
            primaryTestId="profile-save-button"
            secondaryTestId="profile-skip-button"
          />
        )}
      </Box>
    </Box>
  );
}
